import React from 'react';
import { css } from 'emotion';
import { Popover } from 'antd';
import ModalsService from 'services/ModalsService';
import ProcessUtils from 'utils/ProcessUtils';
import NavigationUtils from 'utils/NavigationUtils';
import NewEntityForm from 'components/core/forms/NewEntityForm';
import EditRetrievedVariablesModal from 'components/page/process/variableRetrieval/EditRetrievedVariablesModal';
import DimensionConflictForm from 'components/core/forms/DimensionConflictForm';
import Constants from 'utils/Constants';
import { popoverErrorStyle } from 'styles/base';
import _ from 'lodash';
import $ from "jquery";

/**
 * Utility methods for creating common UI components shared across pages.
 */
export default class ComponentUtils {

    /**
     *
     * @param getComponent - must be a dom element object
     */
    static scrollComponentIntoView(getComponent) {
        // Make sure we use a JQuery object because it computes the offsets better
        const component = $(getComponent());

        // After we upgraded react, the DOM scrollIntoView method would not work correctly, so I've switched to
        // using jquery to animate changing the scroll position on the scrollable container.  In order for
        // this to work on all pages, the scrollable component needs to have the "rie-scrollable" class defined.
        // I am assuming that only one element on the page will have rie-scrollable at a time.  If this changes,
        // we will need to modify this function.
        // See this link for a picture of positioning with scroll window:
        // https://stackoverflow.com/questions/1567327/using-jquery-to-get-elements-position-relative-to-viewport
        let scrollable = $('.rie-scrollable');

        // if there are more than one scrollable, get the last one
        // this could happen if we are in a modal dialog on top of a page
        scrollable = $(scrollable[scrollable.length - 1]);

        if (scrollable.offset()) {
            const config = {};

            // First check if we need to scroll vertically (i.e., if the element is outside the scrollable's viewport)
            const viewportTop = scrollable.scrollTop();
            // Delta is the difference between length of scrollable that is scrolled out of view to the top and
            // the offset to where the scrollable starts in the viewable window
            const viewportTopDelta = scrollable.scrollTop() - scrollable.offset().top;
            const viewportBottom = viewportTop + scrollable.height();
            const elemTop = component.offset().top + viewportTopDelta;

            // We use the scrollHeight to compute where the bottom of the element is because it represents
            // the total height of the component, even if it is outside the viewable window
            const elemBottom = elemTop + component[0].scrollHeight;

            if (elemBottom > viewportBottom) {
                // component bottom is below the viewport, so scroll down the height of the component
                config.scrollTop = viewportTop + component[0].scrollHeight;

            } else if (elemTop < viewportTop) {
                // component top is above the viewport, so scroll up the height of the component
                config.scrollTop = viewportTop - component[0].scrollHeight;
            }

            // Now check if we need to scroll horizontally (i.e., if the element is outside the scrollable's viewport)
            const viewportLeft = scrollable.scrollLeft();
            // Delta is the difference between length of scrollable that is scrolled out of view to the left and
            // the offset to where the scrollable starts in the viewable window
            const viewportLeftDelta = scrollable.scrollLeft() - scrollable.offset().left;
            const viewportRight = viewportLeft + scrollable.width();
            const elemLeft = component.offset().left + viewportLeftDelta;
            const elemRight = elemLeft + component[0].scrollWidth;

            if (elemRight > viewportRight) {
                // component right is to the right of the viewport, so scroll right the width of the component
                config.scrollLeft = viewportLeft + component[0].scrollWidth;

            } else if (elemLeft < viewportLeft) {
                // component left is to the left of the viewport, so scroll left the width of the component
                config.scrollLeft = viewportLeft - component[0].scrollWidth;
            }

            if (!_.isEmpty(config)) {
                scrollable.animate(config, 1000, 'linear');
            }
        }
    }

    static promptDimensionConflictResolution(process, coordinateSystemName, conflicts, okFunction) {
        const modalTitle = 'Dimension Conflict';
        let formComponent = null;
        const formRefFunction = (component) => { formComponent = component; };
        const onOk = () => {
            // Based on what the user picks, modify the variable dimension or coordinate system dimensions accordingly
            const resolutions = formComponent.getResolutions();
            okFunction(resolutions);
        };

        const inputForm = (
            <DimensionConflictForm
                process={process}
                coordinateSystemName={coordinateSystemName}
                conflicts={conflicts}
                ref={formRefFunction}
            />
        );

        ModalsService.openModal({
            id: 'dimension_conflict_resolution',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle,
            modalMessage: inputForm,
            onOk
        });
    }

    /**
     * Controls all the UI to open a 'new' dialog with standard name input field.
     * @param title
     * @param okFunction
     * @param validatorFunction
     * @param config
     */
    static openNewEntityModal(title, okFunction, validatorFunction, config) {
        let options = null;
        let inputLabel = null;
        let modalTitle = `Add ${title}`;
        let multi = false; // only pertains to options
        let creatable = true; // only pertains to options
        if (config) {
            options = config.options;
            inputLabel = config.inputLabel;
            multi = config.multi;
            if (config.creatable === false) {
                creatable = false;
            }
            if (config.modalTitle) {
                modalTitle = config.modalTitle;
            }
        }

        let formComponent = null;
        const formRefFunction = (component) => { formComponent = component; };

        const addOk = () => {
            const newName = formComponent.getNewName();

            let validationResults = null;
            if (creatable) {
                // only need to validate uniqueness if we can type in a new name
                validationResults = validatorFunction(newName, true);

            } else {
                validationResults = validatorFunction(newName);
            }

            if (validationResults.level === null) {
                const returnValue = okFunction(newName) === false ? false : true;
                return returnValue;

            } else {
                formComponent.setInvalid(validationResults.message);
                return false;
            }
        };

        const inputForm = (
            <NewEntityForm
                ref={formRefFunction}
                options={options}
                inputLabel={inputLabel}
                creatable={creatable}
                multi={multi}
                title={title}
            />
        );

        ModalsService.openModal({
            id: 'new_entity',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle,
            modalMessage: inputForm,
            onOk: addOk
        });
    }

    static openDeleteEntitiesModal(process, listPath, selectedItems, deleteValidatorFunction, navigateOnDelete) {

        if (typeof selectedItems === 'string') {
            //if a single string is passed in, convert it to the same format as the list selected objects
            selectedItems = { [selectedItems]: true };
        }

        const validationResults = deleteValidatorFunction(selectedItems);
        const message = <div className="scrollable">{validationResults.message}</div>;

        if (!validationResults.canDelete) {
            ModalsService.openModal({
                id: 'delete_entities',
                modalType: ModalsService.TYPE_ERROR,
                modalTitle: 'Delete Not Allowed',
                modalMessage: message
            });

        } else {
            const deleteOk = () => {
                const editor = ProcessUtils.getEditor(process);
                editor.deleteEntities(listPath, selectedItems);
                editor.commit();

                if (navigateOnDelete) {
                    NavigationUtils.gotoVariableRetrieval(process.name, null);
                }
            };
            ModalsService.openModal({
                id: 'delete_entities',
                modalType: ModalsService.TYPE_CONFIRM,
                modalTitle: 'Confirm Delete',
                modalMessage: message,
                autoFocus: true,
                onOk: deleteOk
            });
        }

    }

    static openMultiEditVariablesModal(process, selectedItems) {
        let formComponent = null;
        const formRefFunction = (component) => { formComponent = component; };

        const form = (
            <EditRetrievedVariablesModal
                ref={formRefFunction}
                selectedItems={selectedItems}
                process={process}
            />
        );

        const className = css`
            .modal-dialog {
                margin-top: 0;
                .modal-body {
                    padding-bottom: 0 !important;
                    padding-top: 10px;
                }
            }
        `;

        // TODO: if user has selected an output datastream that does not exist, then should we navigate to that
        // page so the user can map the variables?
        const onOk = () => {
            const newValues = formComponent.getNewValues();

            //will need to validate units once that is added in
            const validationResults = { level: null, message: null };

            if (validationResults.level === null) {
                const editor = ProcessUtils.getEditor(process);
                const variableNames = Object.keys(selectedItems);

                if (newValues.coordinate_system !== null) {
                    const conflicts = editor.getRetrievedVariableCoordinateSystemConflicts(newValues.coordinate_system, variableNames);

                    if (_.isEmpty(conflicts)) {
                        editor.updateMultipleRetrievedVariables(variableNames, newValues);
                        editor.commit();

                    } else {
                        // We are launching a sub-modal to prompt for dimension conflict
                        ComponentUtils.promptDimensionConflictResolution(process,
                            newValues.coordinate_system,
                            conflicts,
                            (resolutions) => {
                                editor.updateMultipleRetrievedVariables(variableNames, newValues, resolutions);
                                editor.commit();
                            }
                        );
                    }

                } else {
                    editor.updateMultipleRetrievedVariables(variableNames, newValues);
                    editor.commit();
                }
                return true;

            } else {
                formComponent.setInvalid(validationResults.message);
                return false;
            }
        };
        ModalsService.openModal({
            id: 'edit_variables',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Edit Retrieved Variables',
            modalMessage: form,
            modalSize: 'large',
            className,
            onOk
        });
    }

    static openPermissionsModal(processId) {
        ModalsService.openModal({
            id: ModalsService.TYPE_PERMISSIONS,
            processId
        });
    }

    static closePermissionsModal() {
        ModalsService.closeModal(ModalsService.TYPE_PERMISSIONS);
    }

    static openImportWizardModal(processId) {
        ModalsService.openModal({
            id: ModalsService.TYPE_IMPORT_WIZARD,
            processId
        });
    }

    static closeImportWizardModal() {
        ModalsService.closeModal(ModalsService.TYPE_IMPORT_WIZARD);
    }

    //copied and modified to use session storage instead of local so as not to grow indefinitely each time a form is opened
    static getSessionStorageState(id) {
        return JSON.parse(sessionStorage.getItem(id)) || {};
    }

    static setSessionStorageState(id, state) {
        const storedState = this.getSessionStorageState(id);

        Object.keys(state).forEach((key) => {
            storedState[key] = state[key];
        });
        sessionStorage.setItem(id, JSON.stringify(storedState));
    }

    //components that were using local storage to persist UI state were getting re-set
    //whenever the name of the thing the form was editing changed (because that rename
    //also changes the path which is used as the unique id for local storage)
    static renameSessionStorageKey(id, oldName, newName) {
        //predict what new id will be once rename happens:
        const newId = _.replace(id, oldName, newName);
        const storedState = sessionStorage.getItem(id);
        sessionStorage.removeItem(id);
        sessionStorage.setItem(newId, storedState);
    }

    static getVoidFunction() {
        return () => {
            alert('Needs to be implemented.');
        };
    }

    /**
     * wrap component in disabled popover is dependent on write permissions
     * @param component
     * @param hasWritePermissions bool
     * @param className
     * @returns {*}
     */
    static renderPermissionDependentComponent(component, hasWritePermissions, className = null) {
        if (!hasWritePermissions) {
            component = (
                <Popover
                    content={Constants.PERMISSIONS_ERROR_MESSAGE}
                    placement="top"
                    overlayClassName={popoverErrorStyle}
                    className={className}
                >
                    {component}
                </Popover>
            );
        }

        return component;
    }

    static getDisabledMessage(isDeprecated) {
        let message = Constants.PERMISSIONS_ERROR_MESSAGE;
        if (isDeprecated) {
            message = Constants.DEPRECATED_DISABLED_MESSAGE;
        }

        return message
    }

}



// WEBPACK FOOTER //
// ./src/utils/ComponentUtils.js